import React, { ChangeEvent, FormEvent, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio,Checkbox, RadioChangeEvent, Input, Button, DatePicker } from 'antd';
import { Form } from 'react-bootstrap';
import usePortalService from '../services/PortalService';
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ru";
import {TextEditor} from './TextEditor';
import { Company, Vacancy } from '../types';
import { RangePickerProps } from 'antd/es/date-picker';
import { TourProps } from 'antd/lib';
import { UserTour } from './UserTour';
 
const { TextArea } = Input;
const { RangePicker } = DatePicker;

type VacancyEditorProps = {
    vacancy: Vacancy,
    company: Company,
    vacancyId: string,
    setVacancy: (vacancy: Vacancy) => void,
    showPreview: (show: boolean) => void
}

export default function VacancyEditor({vacancy, company, vacancyId, setVacancy, showPreview} : VacancyEditorProps) {
    const navigate = useNavigate();

    const {createVacancy, updateVacancy} = usePortalService();

    const publishButtonRef = useRef(null);
    const previewButtonRef = useRef(null);
    const saveDraftButtonRef = useRef(null);
    
    const [isProbation, setIsProbation] = useState(false);

    const [name, setName] = useState("");
    const [nameDirty, setNameDirty] = useState(false);
    const [nameError, setNameError] = useState('Название не может быть пустым');

    const [status, setStatus] = useState<string>('');

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [datesError, setDatesError] = useState('');

    const [isPaid, setIsPaid] = useState<boolean>(true);

    const [educationLevel, setEducationLevel] = useState<string>('СПО');

    const regOneNumber = /^\d+$/;
    const regRange = /^\d+ ?- ?\d+$/;
    const regUpperBound = /^до \d+$/;
    const regLowerBound = /^от \d+$/;
    const [salary, setSalary] = useState("");
    const [salaryDirty, setSalaryDirty] = useState(false);
    const [salaryError, setSalaryError] = useState('');
    const [minSalary, setMinSalary] = useState<number | null>();
    const [maxSalary, setMaxSalary] = useState<number | null>();

    const [shortDescription, setShortDescription] = useState<string | undefined>("");
    const [fullDescription, setFullDescription] = useState<string | undefined>("");

    const [area, setArea] = useState('');
    const [city, setCity] = useState('');
    const [street, setStreet] = useState('');
    const [house, setHouse] = useState('');

    const descriptionPlaceholder = ``

    const steps: TourProps['steps'] = [
        {
            title: '',
            description: 'Перед публикацией вы можете предварительно посмотреть как будет выглядеть вакансия',
            target: () => previewButtonRef.current
        },
        {
            title: '',
            description: 'Вы можете сохранить черновик вакансии вместо публикации',
            target: () => saveDraftButtonRef.current
        },
        {
            title: '',
            description: 'После заполнения полей, нажмите сюда, чтобы опубликовать вакансию и сделать её видимой для кандидатов',
            target: () => publishButtonRef.current
        }
    ]

    useEffect(() => {
        if (vacancy != null) {
            if (vacancy.name) {
                setName(vacancy.name)
                setNameError('')
            }
            setStatus(vacancy.status)
            setIsPaid(vacancy.isPaid)
            setShortDescription(vacancy.shortDescription)
            setFullDescription(vacancy.fullDescription)
            setArea(vacancy.address?.region)
            setCity(vacancy.address?.city)
            setStreet(vacancy.address?.street)
            setHouse(vacancy.address?.house)
    
            if (vacancy.minSalary && vacancy.maxSalary) {
                setMinSalary(vacancy.minSalary)
                setMaxSalary(vacancy.maxSalary)
                if (vacancy.minSalary !== vacancy.maxSalary) {
                    setSalary(vacancy.minSalary+" - "+vacancy.maxSalary)
                } else {
                    setSalary(String(vacancy.minSalary))
                }
                setSalaryError('')
            } else if (vacancy.minSalary) {
                setMinSalary(vacancy.minSalary)
                setSalary("от "+vacancy.minSalary)
                setSalaryError('')
            } else if (vacancy.maxSalary) {
                setMaxSalary(vacancy.maxSalary)
                setSalary("до "+vacancy.maxSalary)
                setSalaryError('')
            }
    
            setIsProbation(vacancy.isProbation)
            if (vacancy.isProbation) {
                if (vacancy.internshipStartDate) {
                    setStartDate(dayjs(String(vacancy.internshipStartDate).split('T')[0]))
                }
                if (vacancy.internshipEndDate) {
                    setEndDate(dayjs(String(vacancy.internshipEndDate).split('T')[0]))
                }
            } else {
                setEducationLevel(vacancy.education)
            }
        } else if (company != null) {
            if (company != null) {
                setArea(company.region)
                setCity(company.city)
                setStreet(company.street)
                setHouse(company.house)
            }
        }
    }, [vacancy, company]);

    const previewHandler = () => {
        let internshipStartDate, internshipEndDate;
        
        if (isProbation) {
            if (startDate && endDate) {
                internshipStartDate = startDate.year() +'-'+(String(startDate.month()+1).length > 1 ? startDate.month()+1 : ('0'+(startDate.month()+1)))+'-'+(String(startDate.day()).length > 1 ? startDate.day() : ('0'+startDate.day()))
                internshipEndDate = endDate.year()+'-'+(String(endDate.month()+1).length > 1 ? endDate.month()+1 : ('0'+(endDate.month()+1)))+'-'+(String(endDate.day()).length > 1 ? endDate.day() : ('0'+endDate.day()))
            }
        }
        if (nameError || (salaryError && isPaid)) {
            setNameDirty(true)
            setSalaryDirty(true)
        } else {
            setVacancy({
                ...vacancy,
                name,
                status,
                salary: (isPaid ? salary : "Не оплачивается"),
                education: educationLevel,
                shortDescription,
                fullDescription,
                isPaid,
                isProbation,
                minSalary,
                maxSalary,
                internshipStartDate,
                internshipEndDate,
                address: {
                    region:area,
                    city,
                    street,
                    house
                },
                company
            })
            showPreview(true)
        }
    }

    const onTargetStatusChange = (e: RadioChangeEvent) => {
        setIsProbation(e.target.value);
    };
    
    const nameHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        if (e.target.value.length < 1) {
            setNameError('Введите название')
        } else if (e.target.value.length < 2) {
            setNameError('Название не может состоять из одного символа')
        } else {
            setNameError('')
        }
    }

    const educationLevelHandler = (e: ChangeEvent<HTMLSelectElement>) => {
        setEducationLevel(e.target.value)
    };

    const internshipDateHandler: RangePickerProps['onChange'] = (e) => {
        if (e) {
            setStartDate(e[0])
            setEndDate(e[1])
            setDatesError('')
        } else {
            setStartDate(null)
            setEndDate(null)
            // setDatesError('Укажите даты стажировки')
        }
    };

    const salaryHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const strSalary = String(e.target.value);
        setSalary(e.target.value)
        if (regOneNumber.test(String(e.target.value).toLowerCase())) {
            setMinSalary(parseFloat(e.target.value))
            setMaxSalary(parseFloat(e.target.value))
            setSalaryError('')
        } else if (regRange.test(String(e.target.value).toLowerCase())) {
            const sal = strSalary.replace(/\s+/g, '').split('-')
            setMinSalary(parseFloat(sal[0]))
            setMaxSalary(parseFloat(sal[1]))
            setSalaryError('')
        } else if (regUpperBound.test(String(e.target.value).toLowerCase())) {
            const sal = strSalary.split(' ')
            setMinSalary(null)
            setMaxSalary(parseFloat(sal[1]))
            setSalaryError('')
        } else if (regLowerBound.test(String(e.target.value).toLowerCase())) {
            const sal = strSalary.split(' ')
            setMinSalary(parseFloat(sal[1]))
            setMaxSalary(null)
            setSalaryError('')
        } else {
            setMinSalary(null)
            setMaxSalary(null)
            setSalaryError('Корректный формат ввода: "число", "минимум-максимум", "от минимум", "до максимум".')
        }
    };

    const isPaidHandler = () => {
        setIsPaid(!isPaid)
    };

    const areaHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setArea(e.target.value)
    }

    const cityHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setCity(e.target.value)
    }

    const streetHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setStreet(e.target.value)
    }

    const houseHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setHouse(e.target.value)
    }

    const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        switch (e.target.name) {
            case 'nameVacancy':
                setNameDirty(true)
                break;
            case 'salary':
                setSalaryDirty(true)
                if (maxSalary != null && minSalary != null && Number(minSalary) > Number(maxSalary))
                    setSalaryError('Минимальная оплата не может быть выше максимальной')
                break;
        }
    }

    const publishHandler = async () => {
        if (nameError || (salaryError && isPaid)) {
            setNameDirty(true)
            setSalaryDirty(true)
            // setDatesError('Укажите даты стажировки')
        } else {
            if (await createOrUpdate(0)) {
                navigate("/vacancy/"+vacancyId);
            }
        }
    }

    const saveDraftHandler = async (e: FormEvent) => {
        e.preventDefault();
        if (nameError) {
            setNameDirty(true)
        } else {
            if (await createOrUpdate(1)) {
                window.history.back();
            }
        }
    }

    const createOrUpdate = async (status: number) => {
        let internshipStartDate, internshipEndDate = null;
        
        if (isProbation) {
            if (startDate && endDate) {
                internshipStartDate = startDate.year()+'-'+(String(startDate.month()+1).length > 1 ? startDate.month()+1 : ('0'+(startDate.month()+1)))+'-'+(String(startDate.day()).length > 1 ? startDate.day() : ('0'+startDate.day()))
                internshipEndDate = endDate.year()+'-'+(String(endDate.month()+1).length > 1 ? endDate.month()+1 : ('0'+(endDate.month()+1)))+'-'+(String(endDate.day()).length > 1 ? endDate.day() : ('0'+endDate.day()))
            }
        }
        let result;
        if (vacancyId == undefined) {
            if (isProbation) {
                result = await createVacancy({
                    isProbation,
                    name,
                    isPaid,
                    status,
                    minSalary,
                    maxSalary,
                    region:area,
                    city,
                    street,
                    house,
                    shortDescription,
                    detailedDescription: fullDescription,
                    internshipStartDate,
                    internshipEndDate
                });
            } else {
                result = await createVacancy({
                    isProbation,
                    name,
                    isPaid,
                    status,
                    minSalary,
                    maxSalary,
                    region:area,
                    city,
                    street,
                    house,
                    educationRequirement:educationLevel,
                    shortDescription,
                    detailedDescription: fullDescription
                });
            }
        } else {
            result = await updateVacancy({
                vacancyId,
                isProbation,
                name,
                isPaid,
                status,
                minSalary,
                maxSalary,
                region:area,
                city,
                street,
                house,
                educationRequirement:educationLevel,
                shortDescription,
                detailedDescription: fullDescription,
                internshipStartDate,
                internshipEndDate
            });
        }
        if (result.success) {
            vacancyId = result.vacancyId
            return true;
        }
        return false;
    }

    return (
        <>
            <div className="page700" style={{marginBottom:'115px'}}>
                <h1 style={{paddingBottom:'24px'}}>{vacancyId ? 'Редактирование вакансии' : 'Создание вакансии'}</h1>
                <Form>
                    <div className="form-group">
                        <Form.Label style={{marginBottom:'8px'}}> Тип: </Form.Label>
                        <div className="group-select-elements" >
                            <Radio.Group onChange={onTargetStatusChange} value={isProbation}>
                                <Radio value={false}> Работа </Radio>
                                <Radio value={true}> Стажировка </Radio>
                            </Radio.Group>
                        </div>
                    </div>
                    <Form.Group className="form-group" controlId="formSpecializationName">
                        <Form.Label> Название </Form.Label>
                        <Form.Control type="text" name='nameVacancy'
                            value={name} 
                            onBlur={blurHandler} 
                            onChange={nameHandler}
                            isInvalid={!!(nameDirty && nameError)}
                            placeholder="Оператор технологических установок" 
                        />
                        {(nameDirty && nameError) && <p className="error-alert">{nameError}</p>}
                    </Form.Group>
                    <div className="pair-input">
                            {(isProbation) && 
                                <div className="form-group first-in-pair"> 
                                    <Form.Label> Период проведения </Form.Label>
                                    <RangePicker 
                                        value={[startDate, endDate]} 
                                        onChange={internshipDateHandler} 
                                        format='DD-MM-YYYY'/>
                                    {(datesError) && <p className="error-alert">{datesError}</p>}
                                </div>
                            }
                            {(!isProbation) && 
                                <div className="form-group first-in-pair"> 
                                    <Form.Label> Требование к образованию </Form.Label>
                                    <Form.Select /* type='text' */ /* placeholder='Выберите из списка' */ value={educationLevel} onChange={e => educationLevelHandler(e)}>
                                        <option value='СПО'> СПО </option>
                                        <option value='бакалавр ВО'> бакалавр ВО </option>
                                        <option value='магистр ВО'> магистр ВО </option>
                                        <option value='специалист ВО'> специалист ВО </option>
                                        <option value='аспирантура (исследователь, кандидат наук) ВО'> аспирантура (исследователь, кандидат наук) ВО </option>
                                    </Form.Select>
                                </div>
                            }
                        <div className="form-group second-in-pair" style={{marginBottom: '22px'}}>
                            <Form.Label> Зарплата в месяц </Form.Label>
                            <Form.Control type="text" name='salary'
                                value={salary} 
                                onBlur={blurHandler} 
                                onChange={salaryHandler}
                                placeholder="5000-50000"
                                isInvalid={!!(salaryDirty && salaryError && isPaid)} 
                            />
                            {(salaryDirty && salaryError && isPaid) && <p className="error-alert">{salaryError}</p>}
                            <Checkbox checked={!isPaid} onChange={isPaidHandler} className="group-select-elements" style={{marginTop:'4px'}}>
                                Не оплачивается
                            </Checkbox>
                        </div>
                    </div>
                    <h2 style={{marginBottom:'20px'}}> Адрес офиса </h2>
                    <div className="pair-input" style={{maxWidth:'470px'}}>
                        <div className="form-group first-in-pair">
                            <Form.Label> Область </Form.Label>
                            <Form.Control type="text" placeholder="Московская" name='area'
                                value={area} onBlur={blurHandler} onChange={areaHandler} 
                            />
                        </div>
                        <div className="form-group second-in-pair">
                            <Form.Label> Город </Form.Label>
                            <Form.Control type="text" placeholder="Москва" name='city'
                                value={city} onBlur={blurHandler} onChange={cityHandler} 
                            />
                        </div>
                    </div>
                    <div className="pair-input" style={{maxWidth:'470px', marginBottom:'40px'}}>
                        <div className="form-group first-in-pair">
                            <Form.Label> Улица </Form.Label>
                            <Form.Control type="text" placeholder="Гвардейская" name='street'
                                value={street} onBlur={blurHandler} onChange={streetHandler} />
                        </div>
                        <div className="form-group second-in-pair">
                            <Form.Label> Дом </Form.Label>
                            <Form.Control type="text" placeholder="1" name='house'
                                value={house} onBlur={blurHandler} onChange={houseHandler} />
                        </div>
                    </div>
                    <h2 style={{marginBottom:'16px'}}> Описание вакансии </h2>
                    <div className="form-group">
                        <Form.Label> Краткое описание </Form.Label>
                        <TextArea
                            rows={4}
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            placeholder="На Комплекс по производству СПГ требуется молодой специалист на должность 'Оператор т/у'. Необходимо знание основных процессов газопереработки и сжижения. Опыт работы с цифровыми моделями технологических установок является преимуществом."
                        />
                    </div>
                    <div className="form-group">
                        <Form.Label> Подробное описание </Form.Label>
                        <TextEditor 
                            theme="snow" 
                            value={fullDescription} 
                            onChange={setFullDescription} 
                        />
                    </div>
                </Form>
                <footer>
                    <div style={{maxWidth:'700px', width:'100%', marginLeft:'6%', paddingTop: '16px'}}>
                        <div className="different-edges-container">
                            <div className="left-edge">
                                <Button type="primary" onClick={publishHandler} ref={publishButtonRef}> Опубликовать </Button>
                                <Button type="text" onClick={previewHandler} ref={previewButtonRef}> Предпросмотр </Button>
                                <Button type="text" onClick={saveDraftHandler} ref={saveDraftButtonRef}> Сохранить черновик </Button>
                            </div>
                            <div className="right-edge">
                                <button className="grey-button" onClick={() => window.history.back()}> Отмена </button>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
            <UserTour steps={steps} pageKey="vacancyEditor" />
        </>
    )
}